import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import {useAsync} from "react-async-hook";
import {useForm} from 'react-hook-form';
import ReactModal from "react-modal";
import {useAuth} from "../../../../hooks/useAuth";
import {userApi} from "../../../../apis/admin/user/userApi";
import {useSystemMessage} from "../../../../hooks/useSystemMessage";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {ApiClientError} from "../../../../apis/errors";
import useAppRouter from "../../../../hooks/useAppRouter";
import useLoading from "../../../../hooks/useLoading";
import {logError} from "../../../../libs/utils/logError";

interface EmailEditModalProps {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSaved: () => Promise<void>;
}

export interface EmailEditFormValues {
  email: string;
}

export const EmailEditModal = (props: EmailEditModalProps) => {
  const { setShowModal, onSaved} = props;
  const withLoading = useLoading();
  const location = useLocation();
  const { success } = useSystemMessage();
  const [ abortController, setAbortController ] = useState<AbortController | undefined>();
  const [ isInitialized, setIsInitialized ] = useState<boolean>(true);
  const { state: { currentUser }, reloadCurrentUser } = useAuth();
  const [ apiClientError, setApiClientError] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailEditFormValues>({
    reValidateMode: 'onSubmit',
  });

  useAsync(async () => {
    try {
      withLoading(async () => {
        await reloadCurrentUser();
        setValue('email', currentUser!.email);
        setIsInitialized(true);
      });
    } catch {
      goto('internalServerError');
    }
  }, []);

  const handleSendEmail = async (): Promise<void> => {
    withLoading(async () => {
      setApiClientError(null);
      await handleSubmit(async (data: EmailEditFormValues) => {
        try {
          setAbortController(new AbortController());
          await userApi.updateEmail(
            {
              email: data.email,
            },
            {abortSignal: abortController?.signal});
          setAbortController(undefined);
          await reloadCurrentUser();
          await success('メールアドレスを変更しました。');
          await onSaved();

        } catch (e) {
          await logError(
            location.pathname,
            'メールアドレスの変更に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    })
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="form-modal"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">メールアドレス変更</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
          </div>
          <form>
            <div className="form-modal-content">
              {isInitialized && (
                <div className="w-full">
                  <div className="mb-4 text-danger text-xs">
                    { apiClientError?.message }
                    { (Object.keys(errors).length > 0) && '入力エラーがあります' }
                  </div>

                  { /* メールアドレス */ }
                  <div className="mb-4">
                    <label htmlFor="email">
                      <div className="text-xs font-semibold text-primary mb-1">
                        <span>メールアドレス</span>
                        <span className="ml-2 required">必須</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register('email',
                          makeRegisterOptions('メールアドレス', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                            { type: FormValidationType.EMAIL, typeName: 'email' },
                          ])
                        )}
                        id="email"
                        type="text"
                        className="text-field"
                        maxLength={86}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.email?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'email',
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}

              <div className="form-modal-footer-container">
                <div className="form-modal-footer">
                  <button
                    className="btn-sub"
                    type="button"
                    onClick={() => setShowModal(false)}
                  >
                    キャンセル
                  </button>
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={handleSendEmail}
                  >
                    保 存
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};